import ChartController, { Chart } from "./chart_controller"

export default class extends ChartController {
  createChart(ctx) {
    this.createBarChart(ctx)
  }

  createBarChart(ctx) {
    const labels = this.generateDailyLabels()
    const data = this.extractDataValues()
    const { yAxisMax, yAxisStep } = this.calculateYAxisScale(data)

    new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [this.createBarDataset(data)]
      },
      options: {
        ...this.getBaseChartOptions(),
        scales: {
          y: this.createYAxisConfig(yAxisMax, yAxisStep),
          x: this.createBarXAxisConfig(labels)
        },
        plugins: {
          legend: { display: false },
          tooltip: this.createBarTooltipConfig()
        },
        interaction: { intersect: false, mode: "index" },
        layout: this.getDefaultLayout()
      }
    })
  }

  generateDailyLabels() {
    return this.dataValue.map((item) => {
      const dateValue = item.date || item.timestamp
      if (!dateValue) {
        return ""
      }

      const date = dateValue instanceof Date ? dateValue : new Date(dateValue)
      if (isNaN(date.getTime())) {
        return ""
      }

      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    })
  }

  calculateYAxisScale(data) {
    const maxValue = Math.max(...data, 1)
    let yAxisMax = 10
    let yAxisStep = 5

    if (maxValue > 10) {
      yAxisMax = Math.ceil(maxValue * 1.1)
      if (yAxisMax <= 20) {
        yAxisStep = 5
      } else if (yAxisMax <= 50) {
        yAxisStep = 10
      } else if (yAxisMax <= 100) {
        yAxisStep = 20
      } else {
        yAxisStep = Math.ceil(yAxisMax / 6)
      }
    }

    return { yAxisMax, yAxisStep }
  }

  createBarDataset(data) {
    return {
      label: "Views",
      data: data,
      backgroundColor: (context) => this.getBarBackgroundColor(context.parsed.y),
      borderColor: (context) => this.getBarBorderColor(context.parsed.y),
      borderWidth: 1,
      maxBarThickness: 24,
      borderRadius: 4
    }
  }

  getBarBackgroundColor(value) {
    return value === 0 ? "rgba(229, 231, 235, 0.3)" : "rgba(37, 99, 235, 0.8)"
  }

  getBarBorderColor(value) {
    return value === 0 ? "rgba(229, 231, 235, 0.5)" : "rgba(37, 99, 235, 1)"
  }

  createBarTooltipConfig() {
    return {
      enabled: true,
      backgroundColor: "rgba(17, 24, 39, 0.9)",
      titleColor: "rgba(255, 255, 255, 1)",
      bodyColor: "rgba(255, 255, 255, 1)",
      borderColor: "rgba(37, 99, 235, 1)",
      borderWidth: 1,
      padding: 12,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        title: (context) => {
          const title = this.formatTooltipTitle(context[0].dataIndex)
          return title || ""
        },
        label: (context) => `Views: ${context.parsed.y}`
      }
    }
  }

  createBarXAxisConfig(labels) {
    const config = {
      ticks: {
        color: "rgba(107, 114, 128, 0.8)",
        font: { size: 11 }
      },
      grid: { display: false }
    }

    if (labels) {
      config.categoryPercentage = 0.6
      config.barPercentage = 0.9
      config.ticks.maxRotation = 0
      config.ticks.minRotation = 0
      config.ticks.autoSkip = false
      config.ticks.callback = (value, index) => labels[index] || ""
    }

    return config
  }

  formatTooltipTitle(dataIndex) {
    const item = this.dataValue[dataIndex]
    if (!item) {
      return ""
    }

    const dateValue = item.date || item.timestamp
    if (!dateValue) {
      return ""
    }

    const date = dateValue instanceof Date ? dateValue : new Date(dateValue)
    if (isNaN(date.getTime())) {
      return ""
    }

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric"
    })
  }
}
